import { motion } from "framer-motion";
import { Cpu, Workflow, Zap, ShieldCheck, Users, ServerCog, BadgeCheck, Scale, Gauge, RefreshCw, Lock, Database, Atom } from "lucide-react";

const cyberCards = [
  {
    id: "cyber-judgment",
    icon: Users,
    title: "Human Judgment Meets Machine Speed",
    desc: "AI accelerates the analysis. People make the decisions. Together, they shorten the distance between spotting a threat and acting on it with confidence.",
  },
  {
    id: "cyber-resilience",
    icon: ServerCog,
    title: "Resilience Across Operations",
    desc: "Operations can't wait for systems to catch up. We strengthen infrastructure, modernize cyber operations, and improve continuity so teams can adapt through disruption and stay operation-ready.",
  },
  {
    id: "cyber-trust",
    icon: BadgeCheck,
    title: "Trusted AI for Sensitive Operations",
    desc: "Automation only earns its place when people trust it. Every AI recommendation is governed, validated, and reviewed under human oversight before it becomes operation action.",
  },
];

const innovationCards = [
  {
    id: "innovation-governed-ai",
    icon: Scale,
    title: "Governed AI",
    desc: "Trusted AI delivers operational advantage only when it's transparent, governed, and accountable. We build validation, oversight, and human judgment into AI-enabled cyber operations for operationally critical environments.",
  },
  {
    id: "innovation-machine-speed",
    icon: Gauge,
    title: "Cyber Operations at Machine Speed",
    desc: "Cyber teams shouldn't spend their time hunting for problems — they should spend it solving them. Autonomous cyber operations speed up discovery, validation, and remediation, with humans in control.",
  },
  {
    id: "innovation-resilience",
    icon: RefreshCw,
    title: "Cyber Resilience",
    desc: "Resilience isn't measured by avoiding disruption — it's measured by how well operations continue through it. We modernize infrastructure and integrate continuous monitoring to keep critical operations running.",
  },
  {
    id: "innovation-ai-security",
    icon: Lock,
    title: "AI Security",
    desc: "We protect AI models, data, and pipelines from misuse and adversarial threats — giving teams the confidence to deploy AI in critical operations environments.",
  },
  {
    id: "innovation-ai-data",
    icon: Database,
    title: "AI-Ready Data",
    desc: "AI moves at machine speed — but decisions can only move as fast as the data behind them. We connect trusted data, AI, and cyber operations to speed up critical decisions.",
  },
  {
    id: "innovation-quantum",
    icon: Atom,
    title: "Prepared for a Quantum Future",
    desc: "The next cyber challenge won't wait for the next generation of tools. We help organizations prepare now through applied research and emerging technologies.",
  },
];

const caseStudies = [
  {
    id: "case-study-agents",
    img: "/case-agents.png",
    label: "Case Study / AI Agents",
    title: "Agents cut response times and boost coordination",
    desc: "Autonomous AI agents triage, route and resolve inquiries across teams — cutting first-response times by 68% while keeping human operators in command.",
    stat: "68% faster first-response time",
  },
  {
    id: "case-study-federal",
    img: "/case-federal.png",
    label: "Case Study / Generative AI",
    title: "Productivity at federal scale with Gen AI chatbot",
    desc: "A secure generative AI chatbot gives a federal workforce instant, cited answers from thousands of policy and mission documents — saving 40+ hours per employee each year.",
    stat: "40+ hours saved per employee, per year",
  },
];

const whyCards = [
  {
    id: "why-capability",
    icon: Cpu,
    title: "Unparalleled Capability",
    desc: "Engineered for complex, high-stakes environments, our AI systems move beyond analysis to action — enabling greater autonomy, elevated performance, and decisions that are faster and more reliable when it matters most.",
  },
  {
    id: "why-efficiency",
    icon: Workflow,
    title: "Operational Efficiency",
    desc: "Harris Holt AI enhances human-machine collaboration, enabling teams to achieve more without added headcount. By streamlining workflows, integrating with existing infrastructure, and minimizing manual effort, we accelerate execution and improve decision quality.",
  },
  {
    id: "why-action",
    icon: Zap,
    title: "Decisive Action",
    desc: "In high-stakes environments, speed is decisive. Our AI converts data into timely, executable insight and scales seamlessly from pilot to enterprise deployment, enabling leaders to respond in real time, act with greater speed, and address challenges once considered intractable.",
  },
];

export const Expertise = () => (
  <section id="expertise" data-testid="expertise-section" className="py-24 md:py-32 px-6 md:px-12">
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ type: "spring", stiffness: 60, damping: 18 }}
      >
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#0891B2] mb-4">Expertise / AI</p>
        <h2 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight text-zinc-950 max-w-2xl">
          Applied artificial intelligence for the missions that matter
        </h2>
      </motion.div>

      <motion.div
        data-testid="ai-home-capabilities"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ type: "spring", stiffness: 60, damping: 18 }}
        className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
      >
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">AI Home / Capabilities</p>
          <h3 className="text-4xl sm:text-5xl font-heading font-black tracking-tighter leading-none text-zinc-950 uppercase">
            Artificial<br />Intelligence
          </h3>
          <p className="mt-6 text-lg font-heading font-bold tracking-tight text-[#0891B2]">
            Delivering proven AI to solve your biggest challenges.
          </p>
          <p className="mt-5 text-[19px] leading-relaxed text-zinc-600">
            At Harris Holt, artificial intelligence is engineered with a singular
            focus on outcomes, grounded in more than two decades of applied
            experience across defense, intelligence, health, and civil sectors.
            Our proven AI systems are designed to enhance performance, strengthen
            decision-making, and deliver measurable, mission-relevant results.
          </p>
        </div>
        <div className="relative overflow-hidden bg-[#D4D4D8] border border-black/10 min-h-[320px]">
          <img
            src="/ai-engineer.png"
            alt="Harris Holt engineer in a modern technology operations center"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      </motion.div>

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {caseStudies.map((cs, i) => (
            <motion.article
              key={cs.id}
              data-testid={cs.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 60, damping: 18, delay: i * 0.12 }}
              className="group bg-[#D4D4D8] border border-black/10 relative overflow-hidden hover:border-black/30 hover:-translate-y-1 transition-all duration-300"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={cs.img}
                  alt={cs.title}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                />
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#D4D4D8] to-transparent" />
              </div>
              <div className="p-8">
                <p className="font-mono text-sm uppercase tracking-[0.2em] text-[#0891B2] mb-3">{cs.label}</p>
                <h3 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight text-zinc-950">{cs.title}</h3>
                <p className="mt-3 text-[19px] leading-relaxed text-zinc-600">{cs.desc}</p>
                <p data-testid={`${cs.id}-stat`} className="mt-5 inline-flex items-center px-3 py-1 border border-[#0891B2]/40 bg-[#0891B2]/10 font-mono text-sm text-[#0891B2]">
                  {cs.stat}
                </p>
              </div>
            </motion.article>
          ))}
      </div>

      <div data-testid="why-ai-matters" className="mt-24 md:mt-32">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 60, damping: 18 }}
          className="font-mono text-sm uppercase tracking-[0.3em] text-[#0891B2]"
        >
          AI / Why Our AI Matters to Our Customers
        </motion.p>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div
            data-testid="why-intro"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 60, damping: 18 }}
            className="bg-[#D4D4D8] border border-black/10 p-8 hover:border-black/30 hover:-translate-y-1 transition-all duration-300"
          >
            <ShieldCheck size={30} className="text-[#0891B2] mb-6" />
            <h3 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight text-zinc-950">Why Our AI Matters</h3>
            <p className="mt-3 text-[19px] leading-relaxed text-zinc-600">
              Unlocking AI's full potential demands far more than technical
              capability. It requires resilience, secure integration, effective
              human-machine collaboration, and governance frameworks that build
              and sustain trust.
            </p>
          </motion.div>

          {whyCards.map((w, i) => (
            <motion.div
              key={w.id}
              data-testid={w.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 60, damping: 18, delay: (i + 1) * 0.08 }}
              className="bg-[#D4D4D8] border border-black/10 p-8 hover:border-black/30 hover:-translate-y-1 transition-all duration-300"
            >
              <w.icon size={30} className="text-[#0891B2] mb-6" />
              <h3 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight text-zinc-950">{w.title}</h3>
              <p className="mt-3 text-[19px] leading-relaxed text-zinc-600">{w.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <div data-testid="cyber-section" className="mt-24 md:mt-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 60, damping: 18 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
        >
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">Expertise / Cyber</p>
            <h3 className="text-4xl sm:text-5xl font-heading font-black tracking-tighter leading-none text-zinc-950 uppercase">
              Cyber Built to Adapt<br />Operations Built to Endure
            </h3>
            <p className="mt-6 text-[19px] leading-relaxed text-zinc-600">
              Threats move at machine speed. Operations teams need cyber operations
              that can detect, decide, and respond just as fast — without
              sacrificing trust, governance, or control.
            </p>
            <p className="mt-4 text-[19px] leading-relaxed text-zinc-600">
              Harris Holt brings together threat intelligence, AI-enabled
              analysis, and real-world operator experience to help defense and
              intelligence organizations reduce cyber risk, strengthen
              resilience, and keep critical operations moving.
            </p>
          </div>
          <div className="relative overflow-hidden bg-[#D4D4D8] border border-black/10 min-h-[320px]">
            <img
              src="/cyber-engineers.png"
              alt="Harris Holt cyber network operations center"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {cyberCards.map((c, i) => (
            <motion.div
              key={c.id}
              data-testid={c.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 60, damping: 18, delay: i * 0.08 }}
              className="bg-[#D4D4D8] border border-black/10 p-8 hover:border-black/30 hover:-translate-y-1 transition-all duration-300"
            >
              <c.icon size={30} className="text-[#0891B2] mb-6" />
              <h3 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight text-zinc-950">{c.title}</h3>
              <p className="mt-3 text-[19px] leading-relaxed text-zinc-600">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <div data-testid="innovation-section" className="mt-24 md:mt-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 60, damping: 18 }}
        >
          <div className="relative overflow-hidden bg-[#D4D4D8] border border-black/10 h-64 md:h-96">
            <img
              src="/ai-innovation.png"
              alt="Futuristic AI neural network visualization"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <p className="mt-10 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">Expertise / Innovation</p>
          <h3 className="text-4xl sm:text-5xl font-heading font-black tracking-tighter leading-none text-zinc-950 uppercase max-w-3xl">
            Innovation, Driven by Purpose
          </h3>
          <p className="mt-6 text-[19px] leading-relaxed text-zinc-600 max-w-3xl">
            Tomorrow's cyber challenges demand continuous innovation today. We
            combine emerging technologies, real-world experience, and
            purpose-driven operational expertise to help customers adapt faster
            and stay ahead of evolving threats.
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {innovationCards.map((c, i) => (
            <motion.div
              key={c.id}
              data-testid={c.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 60, damping: 18, delay: i * 0.08 }}
              className="bg-[#D4D4D8] border border-black/10 p-8 hover:border-black/30 hover:-translate-y-1 transition-all duration-300"
            >
              <c.icon size={30} className="text-[#0891B2] mb-6" />
              <h3 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight text-zinc-950">{c.title}</h3>
              <p className="mt-3 text-[19px] leading-relaxed text-zinc-600">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
